import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import { toast } from 'sonner';
import Sidebar from '../components/layout/Sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Plus,
  Edit,
  Trash2,
  Upload,
  FileText,
  Building,
  DollarSign,
  Percent,
  Search,
  X
} from 'lucide-react';

const PROPERTY_TYPES = ['SFR', '2-4 Unit', '5+ Unit', 'Mixed Use', 'Commercial'];
const LOAN_TYPES = ['DSCR', 'Fix & Flip', 'Ground Up', 'Bridge', 'Conventional', 'FHA', 'VA'];
const CITIZENSHIP_TYPES = ['US Citizen', 'Permanent Resident', 'Foreign National'];

const LoanProgramsPage = () => {
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [showProgramDialog, setShowProgramDialog] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadingProgramId, setUploadingProgramId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    lender_name: '',
    description: '',
    min_credit_score: '',
    max_credit_score: '',
    min_dscr: '',
    max_ltv: '',
    min_down_payment_percent: '',
    min_loan_amount: '',
    max_loan_amount: '',
    allowed_property_types: [],
    allowed_loan_types: [],
    allowed_citizenship: [],
    interest_rate_min: '',
    interest_rate_max: '',
    term_months: '',
    requirements: '',
    notes: '',
    is_active: true
  });

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await api.get('/loan-programs?active_only=false');
      setPrograms(response.data);
    } catch (error) {
      toast.error('Failed to load loan programs');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      lender_name: '',
      description: '',
      min_credit_score: '',
      max_credit_score: '',
      min_dscr: '',
      max_ltv: '',
      min_down_payment_percent: '',
      min_loan_amount: '',
      max_loan_amount: '',
      allowed_property_types: [],
      allowed_loan_types: [],
      allowed_citizenship: [],
      interest_rate_min: '',
      interest_rate_max: '',
      term_months: '',
      requirements: '',
      notes: '',
      is_active: true
    });
    setEditingProgram(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowProgramDialog(true);
  };

  const openEditDialog = (program) => {
    setEditingProgram(program);
    setFormData({
      name: program.name || '',
      lender_name: program.lender_name || '',
      description: program.description || '',
      min_credit_score: program.min_credit_score || '',
      max_credit_score: program.max_credit_score || '',
      min_dscr: program.min_dscr || '',
      max_ltv: program.max_ltv || '',
      min_down_payment_percent: program.min_down_payment_percent || '',
      min_loan_amount: program.min_loan_amount || '',
      max_loan_amount: program.max_loan_amount || '',
      allowed_property_types: program.allowed_property_types || [],
      allowed_loan_types: program.allowed_loan_types || [],
      allowed_citizenship: program.allowed_citizenship || [],
      interest_rate_min: program.interest_rate_min || '',
      interest_rate_max: program.interest_rate_max || '',
      term_months: program.term_months || '',
      requirements: (program.requirements || []).join('\n'),
      notes: program.notes || '',
      is_active: program.is_active !== false
    });
    setShowProgramDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.lender_name) {
      toast.error('Name and lender are required');
      return;
    }

    const payload = {
      ...formData,
      min_credit_score: formData.min_credit_score ? parseInt(formData.min_credit_score) : null,
      max_credit_score: formData.max_credit_score ? parseInt(formData.max_credit_score) : null,
      min_dscr: formData.min_dscr ? parseFloat(formData.min_dscr) : null,
      max_ltv: formData.max_ltv ? parseFloat(formData.max_ltv) : null,
      min_down_payment_percent: formData.min_down_payment_percent ? parseFloat(formData.min_down_payment_percent) : null,
      min_loan_amount: formData.min_loan_amount ? parseFloat(formData.min_loan_amount) : null,
      max_loan_amount: formData.max_loan_amount ? parseFloat(formData.max_loan_amount) : null,
      interest_rate_min: formData.interest_rate_min ? parseFloat(formData.interest_rate_min) : null,
      interest_rate_max: formData.interest_rate_max ? parseFloat(formData.interest_rate_max) : null,
      term_months: formData.term_months ? parseInt(formData.term_months) : null,
      requirements: formData.requirements.split('\n').filter(r => r.trim())
    };

    try {
      if (editingProgram) {
        await api.put(`/loan-programs/${editingProgram.program_id}`, payload);
        toast.success('Program updated');
      } else {
        await api.post('/loan-programs', payload);
        toast.success('Program created');
      }
      setShowProgramDialog(false);
      resetForm();
      fetchPrograms();
    } catch (error) {
      toast.error('Failed to save program');
    }
  };

  const handleDelete = async (programId) => {
    if (!window.confirm('Are you sure you want to delete this program?')) return;
    
    try {
      await api.delete(`/loan-programs/${programId}`);
      toast.success('Program deleted');
      fetchPrograms();
    } catch (error) {
      toast.error('Failed to delete program');
    }
  };

  const handleUploadPdf = async (event) => {
    const file = event.target.files[0];
    if (!file || !uploadingProgramId) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(`/loan-programs/${uploadingProgramId}/upload-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('PDF uploaded');
      setShowUploadDialog(false);
      setUploadingProgramId(null);
      fetchPrograms();
    } catch (error) {
      toast.error('Failed to upload PDF');
    }
  };

  const toggleArrayField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const filteredPrograms = programs.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.lender_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 p-6 ml-64">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Loan Programs</h1>
              <p className="text-slate-600">Manage available loan programs and eligibility criteria</p>
            </div>
            <Button onClick={openCreateDialog} data-testid="create-program-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Program
            </Button>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Search programs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-programs"
            />
          </div>

          {/* Programs Grid */}
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : filteredPrograms.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Building className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Programs Found</h3>
                <p className="text-slate-600 mb-4">Add your first loan program to get started</p>
                <Button onClick={openCreateDialog}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Program
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredPrograms.map(program => (
                <Card key={program.program_id} className={!program.is_active ? 'opacity-60' : ''} data-testid={`program-card-${program.program_id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{program.name}</CardTitle>
                        <CardDescription>{program.lender_name}</CardDescription>
                      </div>
                      <div className="flex gap-1">
                        {program.pdf_url && (
                          <Button variant="ghost" size="icon" asChild>
                            <a href={program.pdf_url} target="_blank" rel="noopener noreferrer">
                              <FileText className="w-4 h-4" />
                            </a>
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => {
                          setUploadingProgramId(program.program_id);
                          setShowUploadDialog(true);
                        }}>
                          <Upload className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(program)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(program.program_id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    {!program.is_active && <Badge variant="secondary">Inactive</Badge>}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 text-sm">
                      {/* Criteria Summary */}
                      <div className="grid grid-cols-2 gap-2">
                        {program.min_credit_score && (
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">Min Credit:</span>
                            <span className="font-medium">{program.min_credit_score}</span>
                          </div>
                        )}
                        {program.min_dscr && (
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">Min DSCR:</span>
                            <span className="font-medium">{program.min_dscr}</span>
                          </div>
                        )}
                        {program.max_ltv && (
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">Max LTV:</span>
                            <span className="font-medium">{program.max_ltv}%</span>
                          </div>
                        )}
                        {program.min_down_payment_percent && (
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">Min Down:</span>
                            <span className="font-medium">{program.min_down_payment_percent}%</span>
                          </div>
                        )}
                      </div>

                      {/* Loan Amount Range */}
                      {(program.min_loan_amount || program.max_loan_amount) && (
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3 text-slate-400" />
                          <span className="text-slate-500">
                            {program.min_loan_amount ? `$${program.min_loan_amount.toLocaleString()}` : '$0'} - {program.max_loan_amount ? `$${program.max_loan_amount.toLocaleString()}` : 'No max'}
                          </span>
                        </div>
                      )}

                      {/* Property Types */}
                      {program.allowed_property_types?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {program.allowed_property_types.map(type => (
                            <Badge key={type} variant="outline" className="text-xs">{type}</Badge>
                          ))}
                        </div>
                      )}

                      {/* Loan Types */}
                      {program.allowed_loan_types?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {program.allowed_loan_types.map(type => (
                            <Badge key={type} variant="secondary" className="text-xs">{type}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Create/Edit Program Dialog */}
        <Dialog open={showProgramDialog} onOpenChange={setShowProgramDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProgram ? 'Edit Program' : 'Add Loan Program'}</DialogTitle>
              <DialogDescription>Define the loan program parameters and eligibility criteria</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Program Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Prime DSCR 30-Year"
                    data-testid="program-name-input"
                  />
                </div>
                <div>
                  <Label>Lender/Source *</Label>
                  <Input
                    value={formData.lender_name}
                    onChange={(e) => setFormData({ ...formData, lender_name: e.target.value })}
                    placeholder="e.g., ABC Lending"
                    data-testid="program-lender-input"
                  />
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the program..."
                  rows={2}
                />
              </div>

              {/* Credit & DSCR */}
              <div>
                <h4 className="font-medium mb-3">Credit & DSCR Requirements</h4>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label>Min Credit Score</Label>
                    <Input
                      type="number"
                      value={formData.min_credit_score}
                      onChange={(e) => setFormData({ ...formData, min_credit_score: e.target.value })}
                      placeholder="620"
                    />
                  </div>
                  <div>
                    <Label>Max Credit Score</Label>
                    <Input
                      type="number"
                      value={formData.max_credit_score}
                      onChange={(e) => setFormData({ ...formData, max_credit_score: e.target.value })}
                      placeholder="850"
                    />
                  </div>
                  <div>
                    <Label>Min DSCR</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.min_dscr}
                      onChange={(e) => setFormData({ ...formData, min_dscr: e.target.value })}
                      placeholder="1.0"
                    />
                  </div>
                  <div>
                    <Label>Max LTV %</Label>
                    <Input
                      type="number"
                      value={formData.max_ltv}
                      onChange={(e) => setFormData({ ...formData, max_ltv: e.target.value })}
                      placeholder="80"
                    />
                  </div>
                </div>
              </div>

              {/* Loan Amount & Down Payment */}
              <div>
                <h4 className="font-medium mb-3">Loan Parameters</h4>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label>Min Loan Amount</Label>
                    <Input
                      type="number"
                      value={formData.min_loan_amount}
                      onChange={(e) => setFormData({ ...formData, min_loan_amount: e.target.value })}
                      placeholder="100000"
                    />
                  </div>
                  <div>
                    <Label>Max Loan Amount</Label>
                    <Input
                      type="number"
                      value={formData.max_loan_amount}
                      onChange={(e) => setFormData({ ...formData, max_loan_amount: e.target.value })}
                      placeholder="5000000"
                    />
                  </div>
                  <div>
                    <Label>Min Down Payment %</Label>
                    <Input
                      type="number"
                      value={formData.min_down_payment_percent}
                      onChange={(e) => setFormData({ ...formData, min_down_payment_percent: e.target.value })}
                      placeholder="20"
                    />
                  </div>
                  <div>
                    <Label>Term (months)</Label>
                    <Input
                      type="number"
                      value={formData.term_months}
                      onChange={(e) => setFormData({ ...formData, term_months: e.target.value })}
                      placeholder="360"
                    />
                  </div>
                </div>
              </div>

              {/* Interest Rates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Interest Rate Min %</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.interest_rate_min}
                    onChange={(e) => setFormData({ ...formData, interest_rate_min: e.target.value })}
                    placeholder="6.5"
                  />
                </div>
                <div>
                  <Label>Interest Rate Max %</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.interest_rate_max}
                    onChange={(e) => setFormData({ ...formData, interest_rate_max: e.target.value })}
                    placeholder="8.5"
                  />
                </div>
              </div>

              {/* Property Types */}
              <div>
                <Label className="mb-2 block">Allowed Property Types</Label>
                <div className="flex flex-wrap gap-2">
                  {PROPERTY_TYPES.map(type => (
                    <Badge
                      key={type}
                      variant={formData.allowed_property_types.includes(type) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleArrayField('allowed_property_types', type)}
                    >
                      {type}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Loan Types */}
              <div>
                <Label className="mb-2 block">Allowed Loan Types</Label>
                <div className="flex flex-wrap gap-2">
                  {LOAN_TYPES.map(type => (
                    <Badge
                      key={type}
                      variant={formData.allowed_loan_types.includes(type) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleArrayField('allowed_loan_types', type)}
                    >
                      {type}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Citizenship */}
              <div>
                <Label className="mb-2 block">Allowed Citizenship</Label>
                <div className="flex flex-wrap gap-2">
                  {CITIZENSHIP_TYPES.map(type => (
                    <Badge
                      key={type}
                      variant={formData.allowed_citizenship.includes(type) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleArrayField('allowed_citizenship', type)}
                    >
                      {type}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Requirements */}
              <div>
                <Label>Additional Requirements (one per line)</Label>
                <Textarea
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  placeholder="6 months reserves required&#10;No bankruptcies in last 4 years&#10;..."
                  rows={3}
                />
              </div>

              {/* Notes */}
              <div>
                <Label>Internal Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional notes about this program..."
                  rows={2}
                />
              </div>

              {/* Active Status */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_active">Program is active</Label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowProgramDialog(false)}>Cancel</Button>
              <Button onClick={handleSubmit} data-testid="save-program-btn">
                {editingProgram ? 'Update Program' : 'Create Program'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upload PDF Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Program PDF</DialogTitle>
              <DialogDescription>Upload a PDF document for this loan program</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Input
                type="file"
                accept=".pdf"
                onChange={handleUploadPdf}
                data-testid="upload-pdf-input"
              />
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default LoanProgramsPage;
